import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Shield, Search } from "lucide-react";

const RESULT_STYLE = {
    SUCCESS: "bg-emerald-50 text-emerald-700 border-emerald-200",
    DENIED: "bg-red-50 text-red-700 border-red-200",
    FAILED: "bg-red-50 text-red-700 border-red-200",
};

const ACTIONS = ["", "LOGIN", "LOGIN_FAILED", "LOGOUT", "PASSWORD_CHANGE", "PASSWORD_RESET",
    "EMPLOYEE_CREATED", "EMPLOYEE_UPDATED", "EMPLOYEE_ACTIVATED", "EMPLOYEE_DEACTIVATED",
    "DOCUMENT_UPLOADED", "DOCUMENT_RE_UPLOADED", "DOCUMENT_VIEWED", "DOCUMENT_VERIFIED",
    "DOCUMENT_DENIED", "CORRECTION_REQUESTED", "DOCUMENT_ANNOTATED",
    "FORBIDDEN_REQUEST", "UNAUTHORIZED_ACCESS"];

export default function AuditLog() {
    const [logs, setLogs] = useState([]);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState({ action: "", role: "" });

    const load = useCallback(async () => {
        const params = new URLSearchParams();
        if (filter.action) params.set("action", filter.action);
        if (filter.role) params.set("role", filter.role);
        try {
            const { data } = await api.get(`/audit-logs?${params}`);
            setLogs(data);
        } catch (e) { /* silent */ }
    }, [filter.action, filter.role]);
    useEffect(() => { load(); }, [load]);

    const filtered = logs.filter((l) =>
        !q || (l.email + l.action + l.description + (l.employee_id || "")).toLowerCase().includes(q.toLowerCase())
    );

    return (
        <div className="space-y-6" data-testid="audit-page">
            <div>
                <p className="text-[10px] uppercase tracking-[0.28em] text-komma-gold">Security Activity</p>
                <h1 className="font-serif text-3xl text-komma-navy">Audit Log</h1>
                <p className="text-sm text-gray-600 mt-1">Append-only record of every authenticated action across the platform.</p>
            </div>

            <div className="komma-card p-4 flex flex-wrap gap-3 items-center">
                <div className="flex items-center gap-2 flex-1 min-w-[240px]">
                    <Search size={16} className="text-gray-400" />
                    <Input placeholder="Search email, action, description…" value={q} onChange={(e) => setQ(e.target.value)}
                        className="border-0 focus-visible:ring-0" data-testid="audit-search" />
                </div>
                <Select value={filter.action} onValueChange={(v) => setFilter({ ...filter, action: v === "any" ? "" : v })}>
                    <SelectTrigger className="w-56" data-testid="audit-action"><SelectValue placeholder="Any action" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="any">Any action</SelectItem>
                        {ACTIONS.filter(Boolean).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={filter.role} onValueChange={(v) => setFilter({ ...filter, role: v === "any" ? "" : v })}>
                    <SelectTrigger className="w-44" data-testid="audit-role"><SelectValue placeholder="Any role" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="any">Any role</SelectItem>
                        {["ceo", "office_admin", "branch_manager", "marketing_executive", "agent", "telecaller", "customer"].map((r) =>
                            <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            <div className="komma-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-komma-navy/5">
                        <tr>{["When", "Who", "Role", "Action", "Entity", "Description", "IP", "Result"].map((h) => (
                            <th key={h} className="px-3 py-3 text-left text-[10px] uppercase tracking-widest text-komma-navy">{h}</th>
                        ))}</tr>
                    </thead>
                    <tbody>
                        {filtered.map((l) => (
                            <tr key={l.id} className="border-t border-gray-100" data-testid={`audit-row-${l.id}`}>
                                <td className="px-3 py-2 text-xs text-gray-500">{new Date(l.at).toLocaleString()}</td>
                                <td className="px-3 py-2 text-xs">
                                    <p className="text-komma-navy font-semibold">{l.email || "anonymous"}</p>
                                    <p className="text-[10px] font-mono text-gray-500">{l.employee_id || "—"}</p>
                                </td>
                                <td className="px-3 py-2 text-xs capitalize">{(l.role || "").replace("_", " ")}</td>
                                <td className="px-3 py-2">
                                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-komma-navy">
                                        <Shield size={11} className="text-komma-gold" />{l.action}
                                    </span>
                                </td>
                                <td className="px-3 py-2 text-xs text-gray-500">{l.entity || "—"}</td>
                                <td className="px-3 py-2 text-xs text-gray-600 max-w-xs truncate" title={l.description}>{l.description || "—"}</td>
                                <td className="px-3 py-2 text-xs font-mono text-gray-500">{l.ip || "—"}</td>
                                <td className="px-3 py-2">
                                    <Badge variant="outline" className={`${RESULT_STYLE[l.result] || ""} text-[10px]`}>{l.result || "—"}</Badge>
                                </td>
                            </tr>
                        ))}
                        {filtered.length === 0 && <tr><td colSpan="8" className="p-8 text-center text-gray-500">No activity found.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
